import { useEffect, useState } from 'react';

import { fetchRecords } from '../../../services/syntiro_honey/data_ingestion.service';

/** How much of a file (or the whole backlog) a human has approved. */
export interface Completeness {
  /** Records approved so far. */
  approved: number;
  /** Records in the scope (the denominator). */
  total: number;
  /**
   * Records in the scope mapped by a learned rule (the alias loop's payoff, #43)
   * — feeds the "N of M mapped instantly" banner when a file is being reviewed.
   */
  learned: number;
  /**
   * Every record in the scope, including rejected ones — unlike {@link total},
   * which deliberately excludes rejected records so the meter can reach 100%.
   * Destructive actions (e.g. deleting a file) must count this, not `total`,
   * since deletion removes rejected records too.
   */
  allRecordsCount: number;
}

/**
 * Fetches the completeness of a file — approved vs total records — via two
 * count-only queries. Deliberately scoped to `fileId` ONLY, not the reviewer's
 * status/mapping filters: "how done is this workbook" must not change when they
 * filter the list to, say, just the flagged rows. Passing `undefined` measures
 * the whole backlog. Re-fetches when the file or `refreshKey` changes.
 * @param {string | undefined} fileId The file to measure, or undefined for all records.
 * @param {number} refreshKey Bump to re-fetch (the panel bumps it on any mutation).
 * @returns {Completeness | null} The counts, or null until the first load lands.
 */
export function useCompleteness(
  fileId: string | undefined,
  refreshKey: number
): Completeness | null {
  const [completeness, setCompleteness] = useState<Completeness | null>(null);

  useEffect(() => {
    let cancelled = false;
    setCompleteness(null);
    const countOf = async (query: Parameters<typeof fetchRecords>[0]): Promise<number> => {
      const page = await fetchRecords({ ...query, limit: 1, offset: 0 });
      return page.total;
    };
    Promise.all([
      countOf({ fileId }),
      countOf({ fileId, status: 'APPROVED' }),
      countOf({ fileId, status: 'REJECTED' }),
      countOf({ fileId, mappedFromAlias: true })
    ])
      .then(([all, approved, rejected, learned]) => {
        if (!cancelled) {
          // REJECTED records are settled by a human too (the scope model already
          // treats them as such): a file of 8 approved + 2 rejected is DONE.
          // Leaving them in the denominator would pin the meter below 100% forever.
          setCompleteness({ approved, total: all - rejected, learned, allRecordsCount: all });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setCompleteness(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [fileId, refreshKey]);

  return completeness;
}
